import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Film, UserRound } from 'lucide-react';

import { UserAvatar } from '@/components/social/UserAvatar';
import { IconeAvversario } from '@/components/hub/panels/storico/IconeAvversario';
import { useStatoAvversari } from '@/components/hub/panels/storico/contestoStato';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { endDescriptorKey } from '@/lib/qgammon/matchOutcome';
import { dataRelativa, formatPr, type RigaStorico as RigaVM, type StatoSocial } from '@/lib/qgammon/storicoPartite';
import { OSCURA } from '@/lib/v3';

/** Un pezzo della seconda riga. Il puntino NON e' nel testo: sta in un gutter ritagliato, cosi' se il pezzo va a capo il puntino non lo segue. */
function Pezzo({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span className={cn("relative pl-3 before:absolute before:left-1 before:text-fg-muted before:content-['·']", className)}>
      {children}
    </span>
  );
}

/**
 * Una riga dello storico, tavola a2: prima riga avatar · nome · icone, seconda riga esito · motivo · PR · data
 * relativa, a destra il Film. «Interrotta» e «Annullata» non hanno replay; l'«Interrotta» non ha nemmeno le icone.
 */
export function RigaStorico({
  id,
  riga,
  dalLotto,
  adesso,
}: {
  id: string;
  riga: RigaVM;
  /** Lo stato social della PAGINA di questa riga (lotto), o `null`. */
  dalLotto: StatoSocial | null;
  adesso: Date;
}) {
  const { t, i18n } = useTranslation('backgammon');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const stato = useStatoAvversari();
  const { avversario: a } = riga;

  const nome = a.nome ?? t('storico.giocatore');
  const interrotta = riga.esito === 'abandoned';
  const parolaEsito =
    riga.esito === 'won' || riga.esito === 'lost' || riga.esito === 'voided' || riga.esito === 'inprogress'
      ? t(`storico.esito.${riga.esito}`)
      : riga.esito === 'abandoned'
        ? t('endReason.abandoned')
        : '';
  // Il motivo dice qualcosa in piu' solo se e' un'altra parola: sull'«Interrotta» generica sarebbe la stessa due volte.
  const motivo = riga.motivo ? t(endDescriptorKey(riga.motivo), { defaultValue: '' }) : '';
  const motivoDaMostrare = motivo !== '' && motivo !== parolaEsito ? motivo : '';
  const coloreEsito =
    riga.esito === 'won' ? 'text-success' : riga.esito === 'lost' ? 'text-danger' : 'text-fg-muted';

  const apriReplay = () => {
    haptics.selection();
    navigate(`/backgammon/replay/${id}`, { state: { perspective: riga.coloreMio } });
  };
  const apriProfilo = () => {
    if (a.sub) stato.esegui(a.sub, nome, 'profilo', dalLotto);
  };

  // Fuori: il bordo che ritaglia, sul margine sinistro della colonna. Dentro: spostato di un gutter a sinistra, dove
  // stanno i puntini di ogni pezzo — cosi' il pezzo che va a capo parte dal bordo e il suo puntino resta tagliato.
  const dettagli = (
    <span className="block overflow-hidden text-xs text-fg-muted">
      <span className="-ml-3 flex flex-wrap">
        {parolaEsito !== '' && <Pezzo className={cn('font-bold', coloreEsito)}>{parolaEsito}</Pezzo>}
        {motivoDaMostrare !== '' && <Pezzo>{motivoDaMostrare}</Pezzo>}
        {riga.pr != null && (
          <Pezzo>
            {t('hubPanels.pr')} <span className="font-bold text-fg">{formatPr(riga.pr, i18n.language)}</span>
          </Pezzo>
        )}
        <Pezzo>{dataRelativa(riga.data, adesso, i18n.language)}</Pezzo>
      </span>
    </span>
  );

  return (
    <div
      data-testid="storico-riga"
      data-esito={riga.esito}
      className={cn('flex items-center gap-3 rounded-2xl p-3', interrotta ? 'bg-bg-secondary/60' : 'bg-bg-secondary')}
    >
      {/* Avatar e nome aprono il profilo; l'avatar e' una comodita' per il dito, il nome e' il bottone accessibile. */}
      {a.sub ? (
        <button type="button" tabIndex={-1} aria-hidden onClick={apriProfilo} className="shrink-0">
          <Avatar nome={a.nome} url={a.avatarUrl} chiave={a.sub} />
        </button>
      ) : (
        <span className="shrink-0">
          <Avatar nome={a.nome} url={a.avatarUrl} chiave={a.nome ?? id} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          {a.sub ? (
            <button
              type="button"
              onClick={apriProfilo}
              data-testid="storico-nome"
              {...OSCURA}
              className={cn('min-w-0 truncate text-left text-sm font-semibold active:opacity-80', interrotta ? 'text-fg-muted' : 'text-fg')}
            >
              {nome}
            </button>
          ) : (
            <span data-testid="storico-nome" {...OSCURA} className={cn('min-w-0 truncate text-sm font-semibold', interrotta ? 'text-fg-muted' : 'text-fg')}>
              {nome}
            </span>
          )}
          {riga.vsBot && (
            <span className="shrink-0 rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
              {t('hubPanels.bot')}
            </span>
          )}
          {riga.conIcone && a.sub && <IconeAvversario sub={a.sub} nome={nome} dalLotto={dalLotto} />}
        </span>
        {riga.replay ? (
          <button type="button" onClick={apriReplay} className="block w-full text-left">
            {dettagli}
          </button>
        ) : (
          <span className="block">{dettagli}</span>
        )}
        {a.sub && stato.fallito(a.sub) && (
          <span role="alert" className="mt-1 block text-[11px] text-danger">
            {t('storico.gestoFallito')}
          </span>
        )}
      </span>
      {riga.replay && (
        <button
          type="button"
          aria-label={t('hubPanels.replay')}
          onClick={apriReplay}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-fg-muted transition-opacity active:opacity-70"
        >
          <Film aria-hidden className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** L'avatar: la foto se c'e' (oggi mai), l'iniziale del nome, e senza nome («Giocatore») un avatar neutro. */
function Avatar({ nome, url, chiave }: { nome: string | null; url: string | null; chiave: string }) {
  if (nome === null) {
    return (
      <span aria-hidden className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted">
        <UserRound className="h-5 w-5" />
      </span>
    );
  }
  return <UserAvatar userId={chiave} label={nome} avatarUrl={url} size="md" />;
}
